// usePropertyLocks.jsx
// Custom hook for managing property-based smart lock associations

import { useState, useEffect, useCallback } from "react";
import {
  associateLockWithProperty,
  getLocksByProperty,
  getLocksByPropertyForTenant,
  removeLockFromProperty,
  getAllPropertyLocks,
  updateLockWithPropertyInfo,
  getAvailableLocksForProperty,
} from "../services/PropertyLockService";

/**
 * Hook to manage locks for a specific property (supports multiple locks)
 * @param {string} listingId - The property/listing ID
 */
export const usePropertyLock = (listingId) => {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all locks for the property
  const fetchLocks = useCallback(async () => {
    if (!listingId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const locksList = await getLocksByProperty(listingId);
      setLocks(locksList);
    } catch (err) {
      console.error("Error fetching property locks:", err);
      // Don't set error for 404 - just means no locks associated
      if (err.response?.status !== 404) {
        setError(err.message || "Failed to fetch locks");
      }
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  // Associate a lock with the property
  const associateLock = async (lockId) => {
    setError(null);
    try {
      const result = await associateLockWithProperty(listingId, lockId);
      await fetchLocks(); // Refresh locks
      return result;
    } catch (err) {
      console.error("Error associating lock:", err);
      setError(err.message || "Failed to associate lock");
      throw err;
    }
  };

  // Remove a specific lock from the property
  const removeLock = async (lockId) => {
    setError(null);
    try {
      const result = await removeLockFromProperty(listingId, lockId);
      await fetchLocks(); // Refresh locks
      return result;
    } catch (err) {
      console.error("Error removing lock:", err);
      setError(err.message || "Failed to remove lock");
      throw err;
    }
  };

  // Update lock with property information
  const updateLockInfo = async (lockId, propertyInfo) => {
    setError(null);
    try {
      const result = await updateLockWithPropertyInfo(listingId, lockId, propertyInfo);
      await fetchLocks(); // Refresh locks
      return result;
    } catch (err) {
      console.error("Error updating lock info:", err);
      setError(err.message || "Failed to update lock information");
      throw err;
    }
  };

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  return {
    locks,
    loading,
    error,
    associateLock,
    removeLock,
    updateLockInfo,
    refetch: fetchLocks,
  };
};

/**
 * Hook to manage all property-lock associations for the user
 */
export const useAllPropertyLocks = () => {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllAssociations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllPropertyLocks();
      setAssociations(data);
    } catch (err) {
      console.error("Error fetching property locks:", err);
      setError(err.message || "Failed to fetch property-lock associations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllAssociations();
  }, [fetchAllAssociations]);

  return {
    associations,
    loading,
    error,
    refetch: fetchAllAssociations,
  };
};

/**
 * Hook to get available locks for a specific property
 * @param {string} listingId - The property ID to filter locks for
 */
export const useAvailableLocksForProperty = (listingId) => {
  const [availableLocks, setAvailableLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAvailableLocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const locks = await getAvailableLocksForProperty(listingId);
      setAvailableLocks(locks);
    } catch (err) {
      console.error("Error fetching available locks:", err);
      setError(err.message || "Failed to fetch available locks");
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchAvailableLocks();
  }, [fetchAvailableLocks]);

  return {
    availableLocks,
    loading,
    error,
    refetch: fetchAvailableLocks,
  };
};

/**
 * Hook for tenants to fetch locks for their subscribed property
 * @param {string} listingId - The property/listing ID
 */
export const useTenantPropertyLocks = (listingId) => {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch locks for tenant's property
  const fetchLocks = useCallback(async () => {
    if (!listingId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const locksList = await getLocksByPropertyForTenant(listingId);
      setLocks(locksList);
    } catch (err) {
      console.error("Error fetching tenant property locks:", err);
      // Don't set error for 404 - just means no locks associated
      if (err.response?.status !== 404) {
        setError(err.message || "Failed to fetch locks");
      }
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  return {
    locks,
    loading,
    error,
    fetchLocks,
  };
};

export default usePropertyLock;

